import { getDate } from 'date-fns/getDate'
import { addMonths } from 'date-fns/addMonths'
import { fromUnixTime } from 'date-fns/fromUnixTime'
import { getUnixTime } from 'date-fns/getUnixTime'
import { startOfMonth } from 'date-fns/startOfMonth'

import { DebtPriority, INTEREST_TO_DECIMAL, MONTHS } from '../constants/Debts'
import { AccountType, type Account, type Debt, type DebtsCopy, type Goal } from '../types'

const copy = {
  unnamed_label: 'Unnamed Debt',
}

type DataPoint = { x: Date; y: number; payment?: number; extra?: number }
type Dataset = DataPoint[]

export interface DebtChartData extends Debt {
  dataset: Dataset
}

interface MonthlyPayment {
  balance: number
  interest: number
  payment: number
  principal: number
  timestamp: number
}

interface DebtWithPayments extends Debt {
  minimumPayments: MonthlyPayment[]
  minimumFinalPayment?: number | undefined
}

export const transformDebtsChartData = (
  debts: Debt[],
  debtPriority: DebtPriority,
  extraPaydown = 0,
): DebtChartData[] => {
  // Copy debts to avoid overwiting balance during payoff calculation
  const sortedDebts = debts.map((d) => ({ ...d }))
  sortDebtsByPrioriy(sortedDebts, debtPriority)

  const currentDate = new Date()
  const chartData: DebtChartData[] = []
  let monthlyExtra = 0
  let priorityCounter = 1

  // Initialize chartData for each debt
  for (const debt of sortedDebts) {
    chartData.push({
      ...debt,
      // Start with the initial balance
      dataset: [{ x: new Date(currentDate), y: debt.balance }],
    })
  }

  // Process debts using the Snowball method
  while (sortedDebts.some((debt) => debt.balance >= 0.01 && !debt.is_impossible)) {
    let madePayment = false // Track if any payment was made this month
    let hasAppliedSnowballExtra = false // Track if snowball extra has been applied this month
    let hasAppliedExtraPaydown = false // Track if extraPaydown has been applied this month

    for (const [index, debt] of sortedDebts.entries()) {
      // Skip if debt is already paid off
      if (debt.balance <= 0.01) continue

      const dataset = chartData[index].dataset
      const interestRate = (debt.interest_rate ?? 0) / 100
      const monthlyRate = interestRate / 12
      const monthlyInterest = debt.balance * monthlyRate
      const baseMonthlyPayment = debt.monthly_payment ?? 0
      let monthlyPayment = baseMonthlyPayment
      let extraThisMonth = 0

      // Add extra payment to the highest priority unpaid debt (first unpaid debt in sorted order)
      if (!hasAppliedExtraPaydown && extraPaydown > 0) {
        monthlyPayment += extraPaydown
        hasAppliedExtraPaydown = true
      }

      // Add snowball extra payment to the highest priority unpaid debt
      if (!hasAppliedSnowballExtra && monthlyExtra > 0) {
        extraThisMonth = monthlyExtra
        hasAppliedSnowballExtra = true
      }

      // Apply interest before payment
      debt.balance += monthlyInterest
      // Apply monthly payment with any extra payment from snowball
      const appliedPayment = Math.min(debt.balance, monthlyPayment + extraThisMonth)

      // If payment doesn't reduce balance, mark as stagnant
      if (appliedPayment <= monthlyInterest) {
        debt.is_impossible = true
        chartData[index].is_impossible = true
        chartData[index].priority = undefined
        dataset.push({ x: new Date(currentDate), y: debt.balance })
        continue
      }

      debt.balance -= appliedPayment
      // Avoid floating-point precision issues
      debt.balance = debt.balance < 0.01 ? 0 : debt.balance
      // Add a data point for the current debt balance
      dataset.push({
        x: new Date(currentDate),
        y: Math.max(0, debt.balance),
        payment: appliedPayment,
        extra: extraThisMonth,
      })

      // Roll over payments when debt gets paid off (snowball effect)
      // Only roll over the base monthly payment to avoid double-counting extraPaydown
      if (debt.balance <= 0) {
        monthlyExtra += baseMonthlyPayment
        chartData[index].projected_payoff_date = new Date(currentDate)
      }

      if (appliedPayment > 0) madePayment = true
    }

    if (madePayment) currentDate.setMonth(currentDate.getMonth() + 1)
  }

  // Don't project a debt as paid off until missing info is provided
  chartData.forEach((d) => {
    if (d.interest_rate === undefined || d.monthly_payment === undefined) {
      d.projected_payoff_date = undefined
    }
  })

  // Set priorities after all calculations
  chartData.forEach((debt) => {
    debt.priority = !debt.is_paid_off && !debt.is_impossible ? priorityCounter++ : undefined
  })

  return chartData
}

export const buildEmptyStateProps = (
  copy: DebtsCopy,
  hasAggregation: boolean,
  handler: () => void,
) => ({
  description: hasAggregation
    ? copy.empty_state_description
    : copy.empty_state_description_no_aggregation,
  header: hasAggregation ? copy.empty_state_header : copy.empty_state_header_no_aggregation,
  onClickHandler: hasAggregation ? handler : undefined,
  primaryButton: hasAggregation ? copy.connect_accounts : undefined,
})

export function loadDebts(accounts: Account[], goals: Goal[]): Debt[] {
  const debtAccounts = accounts.filter((acc) => {
    // Exclude checking line of credit accounts with positive balance
    // Q: Because CLOC spending is represented by negative? (this logic is from raja)
    if (acc.account_type === AccountType.CHECKING_LINE_OF_CREDIT) {
      return Number(acc.balance) <= 0
    }
    // Exclude explicity excluded acccounts
    return acc.is_excluded_from_debts !== true
  })

  // Creat debts with associated goals
  const goalDebts = goals
    .filter((goal) => debtAccounts.some((acc) => acc.guid === goal.account_guid))
    .map((goal) =>
      createDebt(debtAccounts.find((acc) => acc.guid === goal.account_guid) as Account, goal),
    )

  // Create debts without associated goals, but are based on liabity accounts
  const liabilityDebts = debtAccounts
    .filter((acc) => !goalDebts.some((debt) => debt.account.guid === acc.guid))
    .map((account) => createDebt(account))

  return [...goalDebts, ...liabilityDebts]
}

export function createDebt(account: Account, goal?: Goal): Debt {
  const debt = { account, goal, guid: account.guid } as Debt

  if (goal) {
    // Derive next payment date; default to 1st of month
    const paymentDay = goal.payment_due_at ? getDate(fromUnixTime(goal.payment_due_at)) : 1
    const paymentDueDate = startOfMonth(new Date()).setDate(paymentDay)

    debt.balance = Math.abs(goal.amount - goal.current_amount)
    debt.interest_rate = goal.interest_rate
    debt.is_paid_off = goal.is_complete
    debt.monthly_payment = goal.monthly_payment
    debt.name = goal.name
    debt.original_balance = goal.initial_amount // TODO: Does a debt need this?
    debt.payment_due_date = paymentDueDate
  } else {
    // Derive next payment date; default to 1st of month
    const paymentDay = account.payment_due_at ? getDate(fromUnixTime(account.payment_due_at)) : 1
    const paymentDueDate = startOfMonth(new Date()).setDate(paymentDay)
    const balance = account.balance ?? 0
    const monthlyPayment = account.minimum_payment ?? undefined
    const rate = account.interest_rate ?? undefined

    debt.balance = Math.max(balance, 0) // Don't use overpaid/negative balances for debt calculations
    debt.interest_rate = rate
    debt.is_paid_off = balance === 0
    debt.monthly_payment = monthlyPayment
    debt.name = account?.name ?? copy.unnamed_label
    debt.original_balance = account.original_balance // TODO: Does a debt need this?
    debt.payment_due_date = paymentDueDate
  }

  debt.is_impossible = false
  debt.priority = undefined
  debt.projected_payoff_date = undefined

  return debt
}

export const calculateFinalPayment = (monthlyPayments: MonthlyPayment[]) => {
  const lastPayment = monthlyPayments[monthlyPayments.length - 1] || {}

  return lastPayment.timestamp
}

// Sorts (in place) the passed debts array, according to the given priority
export const sortDebtsByPrioriy = (debts: Debt[], debtPriority: DebtPriority) => {
  // Separate valid debts that can be prioritized from those that can't be

  const [activeDebts, inactiveDebts] = debts.reduce<[DebtWithPayments[], DebtWithPayments[]]>(
    ([active, inactive], debt) => {
      if (debt.is_paid_off || debt.is_impossible) {
        inactive.push({ ...debt, minimumPayments: [] })
      } else {
        active.push(calculateMinimumPayments(debt))
      }
      return [active, inactive]
    },
    [[], []],
  )

  switch (debtPriority) {
    case DebtPriority.FASTEST_PAYOFF_FIRST:
      activeDebts.sort((a, b) => (a.minimumFinalPayment ?? 0) - (b.minimumFinalPayment ?? 0))
      break
    case DebtPriority.HIGHEST_INTEREST:
      activeDebts.sort((a, b) => (b.interest_rate ?? 0) - (a.interest_rate ?? 0))
      break
    case DebtPriority.LOWEST_BALANCE:
      activeDebts.sort((a, b) => a.balance - b.balance)
      break
    case DebtPriority.HIGHEST_BALANCE:
      activeDebts.sort((a, b) => b.balance - a.balance)
      break
    default:
      activeDebts.sort((a, b) => a.balance - b.balance) // Default to lowest balance
  }

  // Reorder debts to begin with sorted/active and end with paid/impossible
  debts.splice(0, debts.length, ...activeDebts, ...inactiveDebts)
}

const calculateMinimumPayments = (debt: Debt): DebtWithPayments => {
  const { monthly_payment, interest_rate, balance, payment_due_date } = debt
  const paymentCount = getPaymentCount(balance, interest_rate ?? 0, monthly_payment ?? 0)

  const minimumPayments = getMonthlyPayments(
    paymentCount,
    balance,
    interest_rate ?? 0,
    monthly_payment ?? 0,
    payment_due_date,
  )

  const minimumFinalPayment = calculateFinalPayment(minimumPayments)

  return {
    ...debt,
    minimumFinalPayment,
    minimumPayments,
  }
}

const getPaymentCount = (
  balance: number,
  interestRate: number | undefined = 0,
  minimumPayment: number | undefined = 0,
) => {
  if (minimumPayment === 0) return 0

  const monthlyInterestRate = interestRate / (MONTHS * INTEREST_TO_DECIMAL)

  return Math.ceil(
    monthlyInterestRate === 0
      ? balance / minimumPayment
      : -Math.log(1 - (monthlyInterestRate * balance) / minimumPayment) /
          Math.log(1 + monthlyInterestRate),
  )
}

const getMonthlyPayments = (
  paymentCount: number,
  startingBalance: number,
  interestRate: number,
  scheduledPayment: number,
  startingDate: number,
) => {
  const monthlyPayments = []
  // If the interest rate is zero, getting the monthly payment is easier, so
  // keep its logic separate from actual interest rate calculations.

  if (interestRate === 0) {
    for (let i = 0; i < paymentCount; i++) {
      const newBalance = startingBalance - scheduledPayment * (i + 1)
      const prevBalance = startingBalance - scheduledPayment * i
      const payment = newBalance >= 0 ? scheduledPayment : prevBalance
      monthlyPayments.push({
        balance: newBalance >= 0 ? newBalance : 0,
        interest: interestRate,
        payment,
        principal: payment,
        timestamp: getUnixTime(
          addMonths(
            fromUnixTime(startingDate), // convert seconds → Date
            i + 1, // add (index + 1) months
          ),
        ),
      })
    }
    return monthlyPayments
  }
  const monthlyRate = interestRate / 1200

  for (let index = 0; index < paymentCount; index++) {
    const timestamp = getUnixTime(addMonths(fromUnixTime(startingDate), index + 1))

    const priorPow = Math.pow(1 + monthlyRate, index)
    const priorBalance =
      startingBalance * priorPow - scheduledPayment * ((priorPow - 1) / monthlyRate)

    const pow = Math.pow(1 + monthlyRate, index + 1)
    const balance = startingBalance * pow - scheduledPayment * ((pow - 1) / monthlyRate)

    const payment = scheduledPayment + Math.min(balance, 0)
    const interest = priorBalance * monthlyRate

    monthlyPayments.push({
      balance: Math.max(0, balance),
      interest,
      payment,
      principal: payment - interest,
      timestamp,
    })
  }

  return monthlyPayments
}
